/**
 * Which provisioner program computed a drift verdict, so a reader can tell a
 * verdict about the program that runs now from one about a program a deploy has
 * since replaced.
 *
 * WHY IT IS NEEDED. A drift verdict is the diff between ONE program and the
 * stored state, and the provisioner Jobs run the backend's own image. So a deploy
 * that changes the program silently changes the diff the next converge applies,
 * while the stored verdict still describes the old one. Measured on staging: a
 * verdict naming one launch template to roll, computed minutes before a deploy
 * bumped the Ubuntu pin, sat beside a converge that rolled five launch templates
 * and a stack it never named.
 *
 * WHY NOT A TIMESTAMP. A rolling deploy runs both images at once, so an old pod
 * can observe after a new pod started; "observed after the deploy" then reads
 * fresh about a program that is being replaced. Only the revision itself carries
 * the fact.
 *
 * TWO COPIES OF ONE REVISION, AND ONE OF THEM DECIDES. The drift Job reports the
 * revision beside its verdict on EVERY outcome, and the reconcile loop records it
 * in `VibeTenantCluster.lastDriftProgramRevision`. That column is what
 * `classifyDriftVerdictProgram` reads: a converged or absent verdict writes no
 * summary, so a line in the summary could never attribute a HEALTHY cluster, and
 * free text parsed back out of `statusReason` is not a value to decide on. A
 * drifted summary still carries `computed by: <revision>` as its program line —
 * the human copy, for whoever reads the reason itself. Both copies come from the
 * one revision the fold is handed, and `toDriftProgramRevision` is the one shape
 * either may hold: the column's CHECK refuses anything else, and the line's
 * parser reads anything else as unread.
 */
export const DRIFT_PROGRAM_LINE_PREFIX = "computed by: ";

/** One or more non-whitespace characters — the column's CHECK, spelled once. */
const PROGRAM_REVISION = /^\S+$/;

/**
 * A program revision as either copy may hold it, or null — the one spelling of
 * "no revision". Trims, then refuses empty and whitespace-bearing values: the
 * column's CHECK (`VibeTenantCluster_last_drift_program_revision_shape`) refuses
 * them, so a revision that reached a write unnormalised would refuse the whole
 * UPDATE it rode on, and the program line's parser reads them as unread.
 */
export function toDriftProgramRevision(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  return trimmed !== undefined && PROGRAM_REVISION.test(trimmed) ? trimmed : null;
}

/** `computed by: <the image the drift Job ran>`. */
export function formatDriftProgramLine(programRevision: string): string {
  return `${DRIFT_PROGRAM_LINE_PREFIX}${programRevision}`;
}

const PROGRAM_LINE = /^computed by: (\S+)$/;

export type DriftProgramReading =
  | { readonly kind: "stamped"; readonly programRevision: string }
  | { readonly kind: "unread" };

/**
 * Read the program back off a summary or a `statusReason` — the human copy.
 * `unread` for a null reason, a summary written before the line existed, a
 * malformed line, or two lines.
 */
export function readDriftProgram(summary: string | null): DriftProgramReading {
  if (summary === null) return { kind: "unread" };
  const lines = summary.split("\n").filter((line) => line.startsWith(DRIFT_PROGRAM_LINE_PREFIX));
  const match = lines.length === 1 ? PROGRAM_LINE.exec(lines[0] ?? "") : null;
  const programRevision = match?.[1];
  return programRevision === undefined ? { kind: "unread" } : { kind: "stamped", programRevision };
}

/**
 * Whether a cluster's most recent drift verdict was computed by the program
 * running now.
 *
 * - `current`   — computed by the running program.
 * - `different` — computed by another program: a different deploy, so it may
 *                 understate (or overstate) what the next converge applies.
 *                 Re-observe before reading it.
 * - `unread`    — no verdict program on record, or no running program known to
 *                 compare against. Never read as `current`.
 */
export const DRIFT_VERDICT_PROGRAMS = ["current", "different", "unread"] as const;

export type DriftVerdictProgram = (typeof DRIFT_VERDICT_PROGRAMS)[number];

/**
 * Classify the RECORDED verdict program — `lastDriftProgramRevision`, never a
 * summary — against the running one. Both are read through
 * `toDriftProgramRevision`, so a blank on either side is unread rather than a
 * comparison of blanks.
 */
export function classifyDriftVerdictProgram(
  verdictProgramRevision: string | null,
  runningProgramRevision: string | null
): DriftVerdictProgram {
  const verdict = toDriftProgramRevision(verdictProgramRevision);
  const running = toDriftProgramRevision(runningProgramRevision);
  if (verdict === null || running === null) return "unread";
  return verdict === running ? "current" : "different";
}
